/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React, { use } from 'react';
import { LinkRewardsContext } from '@/components/LinkRewardsContext';
import dynamic from 'next/dynamic';
import DynamicModal from './DynamicModal';
import styles from './StandaloneOnlineMergeModals.module.scss';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { LoadingSpinner } from './LoadingSpinner/LoadingSpinner';
import { useLoginConfirmation } from '@/features/account/hooks/useLoginConfirmation';
import type { StandaloneOnlineMergeModalsType } from '../interface';

const LazySignMeUpFormModal = dynamic(
  () => import('./sign_me_up_form').then(({ SignMeUpFormModal }) => SignMeUpFormModal),
  {
    loading: () => <LoadingSpinner />,
  },
);

const LazyEnterRewardsIdFormModal = dynamic(
  () =>
    import('./enter_rewards_id_form').then(
      ({ EnterRewardsIdFormModal }) => EnterRewardsIdFormModal,
    ),
  {
    loading: () => <LoadingSpinner />,
  },
);

const LazySearchCriteriaFound = dynamic(
  () => import('./search_criteria_found').then(({ SearchCriteriaFound }) => SearchCriteriaFound),
  {
    loading: () => <LoadingSpinner />,
  },
);

const LazyShowRewardConfirmationCongrats = dynamic(
  () =>
    import('./show_reward_confirmation_congrats').then(
      ({ ShowRewardConfirmationCongrats }) => ShowRewardConfirmationCongrats,
    ),
  {
    loading: () => <LoadingSpinner />,
  },
);

const LazyLinkRewardsAccountForm = dynamic(
  () =>
    import('./link_rewards_account_form').then(
      ({ LinkRewardsAccountForm }) => LinkRewardsAccountForm,
    ),
  {
    loading: () => <LoadingSpinner />,
  },
);

const LazyRewardSignInModal = dynamic(
  () =>
    import('./RewardSigninModal/RewardSigninModal').then(
      ({ RewardSignInModal }) => RewardSignInModal,
    ),
  {
    loading: () => <LoadingSpinner />,
  },
);

const LazyLinkRewardsAccountModal = dynamic(
  () =>
    import('./LinkRewardsAccountModal/LinkRewardsAccountModal').then(
      ({ LinkRewardsAccountModal }) => LinkRewardsAccountModal,
    ),
  {
    loading: () => <LoadingSpinner />,
  },
);

const LazyValidateRewardsModalV2 = dynamic(
  () =>
    import('./ValidateRewardsModal/ValidateRewardsStandaloneModal').then(
      ({ ValidateRewardsStandaloneModal }) => ValidateRewardsStandaloneModal,
    ),
  {
    loading: () => <LoadingSpinner />,
  },
);

type Props = {
  view: StandaloneOnlineMergeModalsType;
};

export function StandaloneOnlineMergeModals({ view }: Props) {
  const { clearModal, setGlobalError, handleBack } = use(LinkRewardsContext);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const { changePopupType } = useLoginConfirmation();

  const onClose = () => {
    setGlobalError(null);
    changePopupType({ type: 'HIDE_POPUP' });
    clearModal();
  };

  const getCurrentModal = () => {
    switch (view) {
      case 'SHOW_REWARD_SIGNIN':
        return <LazyRewardSignInModal />;

      case 'SIGN_ME_UP_FORM':
        return <LazySignMeUpFormModal />;

      case 'LINK_REWARDS_ACC':
        return <LazyLinkRewardsAccountModal />;

      case 'ENTER_REWARDS_ID_FORM':
        return <LazyEnterRewardsIdFormModal />;

      case 'LINK_REWARDS_ACC_FORM':
        return <LazyLinkRewardsAccountForm />;

      case 'SEARCH_CRITERIA_FOUND':
        return <LazySearchCriteriaFound />;

      case 'VALIDATE_REWARD_QUESTIONS':
        return <LazyValidateRewardsModalV2 />;

      case 'SHOW_REWARD_CONFIRMATION_CONGRATS':
        return <LazyShowRewardConfirmationCongrats />;
    }
  };

  return (
    <DynamicModal
      closeByClickAway={false}
      headerColor={isMobile ? 'primary' : 'secondary'}
      isOpen
      onClose={onClose}
      hasCloseButton={true}
      hasHeaderBackButton={
        view !== 'SHOW_REWARD_SIGNIN' && view !== 'SHOW_REWARD_CONFIRMATION_CONGRATS'
      }
      backButtonTitle="BACK"
      onBackClick={handleBack}
      className={styles.dynamicModalWidth}
      body={getCurrentModal()}
    />
  );
}
